import React, { useEffect, useMemo, useState } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import { useStopImpersonation } from '../../hooks/api/useImpersonation';

// Hard cutoff so a forgotten session can't run forever. The banner shows a
// countdown so the operator knows how long they have left.
const SESSION_LIMIT_MS = 30 * 60 * 1000;

function formatRemaining(remainingMs: number): string {
  if (remainingMs <= 0) return '0:00';
  const totalSec = Math.floor(remainingMs / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

/**
 * Sticky-top banner shown whenever a super-admin is viewing another org's
 * data. Keeps the impersonation context obvious so we never confuse "their
 * data" with "my data".
 */
const ImpersonationBanner: React.FC = () => {
  const impersonatingOrg = useAuthStore(s => s.impersonatingOrg);
  const stop = useStopImpersonation();

  // Tick once a second to keep the countdown live. The `tick` counter is the
  // synthetic dependency that causes `remaining` to recompute on each frame
  // -- the previous version put `Math.floor(Date.now()/1000)` directly in the
  // deps array, which the react-hooks/exhaustive-deps lint rule rightly
  // rejects (deps must be simple expressions).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!impersonatingOrg) return;
    const id = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(id);
  }, [impersonatingOrg]);

  const remaining = useMemo(() => {
    if (!impersonatingOrg) return 0;
    const elapsed = Date.now() - impersonatingOrg.startedAt;
    return Math.max(0, SESSION_LIMIT_MS - elapsed);
    // `tick` invalidates the memo each second -- Date.now() isn't a real dep.
  }, [impersonatingOrg, tick]);

  // Auto-stop when we hit the hard limit -- the server will reject mutating
  // requests anyway, but the UI banner should reflect reality.
  useEffect(() => {
    if (impersonatingOrg && remaining === 0) {
      stop.mutate();
    }
  }, [impersonatingOrg, remaining, stop]);

  if (!impersonatingOrg) return null;

  return (
    <div
      role="alert"
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 w-full bg-amber-500 text-amber-950 dark:text-amber-50 dark:bg-amber-600 shadow-md"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3">
        <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
        <div className="flex-1 text-sm">
          <span className="font-semibold">
            Viewing as {impersonatingOrg.name}
          </span>
          <span className="opacity-80">
            {' '}
            · read-only · <span data-testid="impersonation-remaining">{formatRemaining(remaining)}</span> remaining
          </span>
        </div>
        <button
          type="button"
          onClick={() => stop.mutate()}
          disabled={stop.isPending}
          className="inline-flex items-center gap-1 px-3 py-1 rounded text-xs font-medium bg-amber-700 hover:bg-amber-800 text-amber-50 disabled:opacity-60 transition-colors"
        >
          <XMarkIcon className="w-4 h-4" aria-hidden="true" />
          {stop.isPending ? 'Stopping…' : 'Stop viewing'}
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
